/** API may return a bare array or `{ data: [...] }`. */
export function normalizeCommentsResponse(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  if (res && Array.isArray(res.comments)) return res.comments;
  return [];
}

/** Raw API value → epoch ms, or null if unusable. */
export function commentCreatedAtMs(c) {
  if (!c || typeof c !== "object") return null;
  const raw =
    c.created_at ??
    c.createdAt ??
    c.Created_at ??
    c.updated_at ??
    c.updatedAt;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw > 0 && raw < 1e12) return raw * 1000;
    return raw;
  }
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Numeric id for tie-break; string ids compare lexicographically. */
function commentIdSortKey(c) {
  const id = c?.id;
  if (id == null) return { type: "n", v: 0 };
  const n = Number(id);
  if (Number.isFinite(n)) return { type: "n", v: n };
  return { type: "s", v: String(id) };
}

function compareIdKeys(ka, kb) {
  if (ka.type === "n" && kb.type === "n") return ka.v - kb.v;
  return String(ka.v).localeCompare(String(kb.v));
}

/** Oldest first, newest last (for chat-style UI). */
export function sortThreadCommentsAsc(comments) {
  if (!Array.isArray(comments)) return [];
  return [...comments].sort((a, b) => {
    const ta = commentCreatedAtMs(a);
    const tb = commentCreatedAtMs(b);
    if (ta != null && tb != null && ta !== tb) return ta - tb;
    if (ta != null && tb == null) return -1;
    if (ta == null && tb != null) return 1;
    return compareIdKeys(commentIdSortKey(a), commentIdSortKey(b));
  });
}

const DEFAULT_SCROLL_THRESHOLD_PX = 8;

/** True if content overflows and the bottom edge is not visible. */
export function threadCommentsBottomBelowFold(
  el,
  thresholdPx = DEFAULT_SCROLL_THRESHOLD_PX
) {
  if (!el || el.scrollHeight <= el.clientHeight) return false;
  return el.scrollTop + el.clientHeight < el.scrollHeight - thresholdPx;
}

export function scrollThreadCommentsToBottom(el) {
  if (el) el.scrollTop = el.scrollHeight;
}

/**
 * Popovers / accordions often mount the scroll container after the first layout pass, so
 * `ref.current` is still null in useLayoutEffect. Deferred passes pick up real scrollHeight.
 */
export function scheduleScrollThreadCommentsToBottom(getElement) {
  if (typeof window === "undefined" || typeof getElement !== "function") return;
  const run = () => {
    const el = getElement();
    if (el) scrollThreadCommentsToBottom(el);
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
  window.setTimeout(run, 0);
  window.setTimeout(run, 80);
}
